/** Sprint 5-b PWA · 語音會議室（開始錄音 + 連續 STT + 30s chunk 上傳 + 成本 → 結束 digest）。 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Paper, Stack, Typography,
} from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import StopIcon from '@mui/icons-material/Stop';

import { useMessage } from '../../../contexts/MessageContext';
import { formatLocal } from '../../../utils/dateHelper';
import { getEvent } from '../../../services/eeMyCalendarService';
import { EeMyCalendar } from '../../../types/eeMyCalendar';
import { useContinuousSpeech } from '../../../hooks/useContinuousSpeech';
import { startRecording, uploadChunk, stopRecording, estimateCost } from './services/voiceCalendarService';

const CHUNK_MS = 30000;

const MeetingRoomPage: React.FC = () => {
  const { eventId } = useParams();
  const eid = Number(eventId);
  const nav = useNavigate();
  const { t } = useTranslation();
  const tt = (k: string, d: string) => t(`pwaMeeting.${k}`, d);
  const { showApiError } = useMessage();
  const { supported, recording, interim, start, stop } = useContinuousSpeech('zh-TW');

  const [ev, setEv] = useState<EeMyCalendar | null>(null);
  const [warnOpen, setWarnOpen] = useState(false);
  const [estNtd, setEstNtd] = useState<number | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [lines, setLines] = useState<string[]>([]);
  const [processing, setProcessing] = useState(false);

  const bufRef = useRef<string[]>([]);       // 未上傳的 final 段落
  const chunkIdxRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => { getEvent(eid).then(setEv).catch(showApiError); }, [eid, showApiError]);
  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current);
    if (tickRef.current) clearInterval(tickRef.current); }, []);

  const flush = useCallback(async () => {
    if (bufRef.current.length === 0) return;
    const text = bufRef.current.join(' ');
    bufRef.current = [];
    const idx = chunkIdxRef.current++;
    try { await uploadChunk(eid, idx, text, CHUNK_MS); } catch { /* silent ok: 下輪重試/最終補送 */ }
  }, [eid]);

  const openWarn = async () => {
    try {
      const est = await estimateCost(30);   // 預設估 30 分（依實際時長計費）
      setEstNtd(est.estimated_ntd);
    } catch { setEstNtd(null); }
    setWarnOpen(true);
  };

  const begin = async () => {
    setWarnOpen(false);
    try { await startRecording(eid); } catch (err) { showApiError(err); return; }
    chunkIdxRef.current = 0; bufRef.current = []; setLines([]); setElapsed(0);
    start((text) => { if (text) { bufRef.current.push(text); setLines((p) => [...p, text]); } });
    timerRef.current = setInterval(flush, CHUNK_MS);
    tickRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
  };

  const end = async () => {
    stop();
    if (timerRef.current) clearInterval(timerRef.current);
    if (tickRef.current) clearInterval(tickRef.current);
    await flush();                            // 送出最後一段
    setProcessing(true);
    try {
      await stopRecording(eid);               // 觸發 final digest
      nav(`/ee_pwa_meetings/${eid}/minutes`);
    } catch (err) { showApiError(err); setProcessing(false); }
  };

  const mmss = `${String(Math.floor(elapsed / 60)).padStart(2, '0')}:${String(elapsed % 60).padStart(2, '0')}`;

  return (
    <Box sx={{ p: 2, maxWidth: 640, mx: 'auto' }}>
      <Button size="small" onClick={() => nav('/ee_pwa_calendar')}>← {tt('back', '返回')}</Button>
      <Paper variant="outlined" sx={{ p: 1.5, my: 1 }}>
        <Typography variant="h6">{ev?.summary || tt('meeting', '會議')}</Typography>
        <Typography variant="caption" color="text.secondary">
          {ev?.dtstart ? formatLocal(ev.dtstart) : ''} {ev?.location ? `· ${ev.location}` : ''}</Typography>
      </Paper>

      {!supported && <Alert severity="warning" sx={{ mb: 1 }}>{tt('noStt', '此瀏覽器不支援語音辨識。')}</Alert>}

      {!recording && !processing && (
        <Button variant="contained" fullWidth size="large" startIcon={<FiberManualRecordIcon />}
          onClick={openWarn} disabled={!supported} sx={{ py: 1.5 }}>
          {tt('startRec', '開始會議紀錄')}</Button>
      )}

      {recording && (
        <>
          <Stack direction="row" alignItems="center" justifyContent="space-between"
            sx={{ p: 1, bgcolor: 'error.main', color: '#fff', borderRadius: 1 }}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <FiberManualRecordIcon sx={{ fontSize: 14, animation: 'blink 1s infinite' }} />
              <Typography variant="body2">{tt('recording', '錄音中')} · {mmss}</Typography>
            </Stack>
            <Typography variant="caption">
              {tt('estCost', '預估')} ~NT${estNtd != null ? estNtd.toFixed(0) : '?'} · {tt('billNote', '依實際時長計費')}</Typography>
          </Stack>
          <Paper variant="outlined" sx={{ p: 1.5, my: 1, maxHeight: 320, overflowY: 'auto' }}>
            {lines.map((l, i) => <Typography key={i} variant="body2" sx={{ mb: 0.5 }}>{l}</Typography>)}
            {interim && <Typography variant="body2" color="text.secondary">{interim}…</Typography>}
            {lines.length === 0 && !interim && <Typography variant="body2" color="text.secondary">
              {tt('waiting', '開始說話，逐字稿會即時顯示…')}</Typography>}
          </Paper>
          <Button variant="contained" color="warning" fullWidth startIcon={<StopIcon />} onClick={end}>
            {tt('stopRec', '結束並產生紀錄')}</Button>
        </>
      )}

      {processing && (
        <Stack alignItems="center" spacing={1} sx={{ py: 4 }}>
          <CircularProgress />
          <Typography variant="body2">{tt('digesting', 'AI 整理會議紀錄中…')}</Typography>
        </Stack>
      )}

      <style>{'@keyframes blink{50%{opacity:.3}}'}</style>

      <Dialog open={warnOpen} onClose={() => setWarnOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{tt('warnTitle', '開始會議紀錄')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {tt('warnBody', '將啟動錄音 + AI 產生會議紀錄，依實際時長計費')}
            （{tt('warnEst', '估計')} ~NT${estNtd != null ? estNtd.toFixed(0) : '70'}/{tt('perHour', '小時')}）。</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWarnOpen(false)}>{tt('cancel', '取消')}</Button>
          <Button variant="contained" onClick={begin}>{tt('confirmStart', '確認開始')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MeetingRoomPage;
